/**
 * `/plans/nowy` with no plan in this tab — a reload, a crash, a second tab. When this device kept
 * the unsaved plan the notice asks whether to restore it; when it did not — another browser,
 * storage blocked, a week gone — it says the plan is gone, and never seeds a blank one nobody
 * described. While the signed-in user is still unconfirmed it waits, like any other read.
 *
 * The notice chrome is this page's `BuilderNotice`, the same box every non-week state stands in; the
 * decision and the plan's card and answers are `features/create-plan`'s. „Usuń program" removes
 * its own button, so focus is put on the heading first — the one element that stays, and which is
 * about to say there is nothing left to restore.
 */

import { useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { LeftoverPlanAnswers, useUnsavedPlanRecovery } from '~/features/create-plan';

import { BuilderNotice } from './BuilderNotice';

export function NewPlanNotice({ escape }: { readonly escape: ReactNode }) {
  const { t } = useTranslation();
  const recovery = useUnsavedPlanRecovery();
  const heading = useRef<HTMLHeadingElement>(null);

  if (recovery.status === 'pending') {
    return <BuilderNotice title={t('web:planBuilder.loading')} busy />;
  }
  if (recovery.status === 'nothing') {
    return (
      <BuilderNotice
        titleRef={heading}
        title={t('web:planBuilder.states.noDraftTitle')}
        message={t('web:planBuilder.states.noDraftMessage')}
        escape={escape}
      />
    );
  }
  return (
    <BuilderNotice
      titleRef={heading}
      title={t('web:planResume.restoreTitle')}
      message={t('web:planResume.restoreBody')}
    >
      <LeftoverPlanAnswers
        plan={recovery.plan}
        onRestore={recovery.restore}
        onRemove={() => {
          heading.current?.focus();
          recovery.remove();
        }}
      />
    </BuilderNotice>
  );
}
